<script setup lang="ts">
import type { Media } from "@/models"
import type { IMenuItem } from "@/types"
import { computed } from "vue"
import { Checkbox, DropdownMenu, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { MediaType } from "@/models"

interface MediaImageTileProps {
  media: Media
  isSelected: boolean
  isTaggingEnabled?: boolean
}

interface MediaImageTileEmits {
  (e: "select", media: Media): void
  (e: "deselect", media: Media): void
  (e: "move", media: Media): void
  (e: "tag", media: Media): void
  (e: "delete", media: Media): void
  (e: "view", media: Media): void
  (e: "approve", media: Media): void
  (e: "reject", media: Media): void
  (e: "favourite", media: Media): void
}

const props = defineProps<MediaImageTileProps>()
const emit = defineEmits<MediaImageTileEmits>()
const { t } = useTranslation()

const isVideo = computed(() => props.media.type === MediaType.Vid)

function handleCheckboxChange(checked: boolean) {
  if (checked) {
    emit("select", props.media)
  }
  else {
    emit("deselect", props.media)
  }
}

const dropdownItems = computed(() => {
  const moderateItems = [
    {
      key: "approve",
      label: t("media_table.approve"),
      icon: "CircleCheck" as const,
      onClick: () => emit("approve", props.media),
      dismissOnInteract: true,
    },
    {
      key: "reject",
      label: t("media_table.reject"),
      icon: "CircleRemove" as const,
      variant: "danger" as const,
      onClick: () => emit("reject", props.media),
      dismissOnInteract: true,
    },
  ]

  const items: IMenuItem[] = [
    {
      key: "view",
      label: t("media_table.media_tile.view"),
      icon: "ImageOn",
      onClick: () => emit("view", props.media),
      dismissOnInteract: true,
    },
    {
      key: "favourite",
      label: props.media?.isPinned ? t("media_table.media_tile.favourite_remove") : t("media_table.media_tile.favourite"),
      icon: props.media?.isPinned ? "StarOff" : "Star",
      onClick: () => emit("favourite", props.media),
      dismissOnInteract: true,
    },
    {
      key: "moderate",
      label: t("media_table.review"),
      icon: "EyeOn" as const,
      isHidden: !props.media?.requiresModeration,
      children: moderateItems,
    },
    {
      key: "move",
      label: t("media_table.media_tile.move"),
      icon: "MoveToFolder" as const,
      onClick: () => emit("move", props.media),
      dismissOnInteract: true,
    },
    {
      key: "tag",
      label: t("media_table.media_tile.add_tags"),
      icon: "Tag" as const,
      onClick: () => emit("tag", props.media),
      dismissOnInteract: true,
      isHidden: !props.isTaggingEnabled,
    },
    {
      key: "delete",
      label: t("media_table.delete"),
      icon: "Trash" as const,
      variant: "danger" as const,
      onClick: () => emit("delete", props.media),
      dismissOnInteract: true,
    },
  ]
  return items
})
</script>

<template>
  <div
    class="relative group bg-white rounded-lg overflow-hidden border transition-all hover:shadow-sm"
    :class="isSelected ? 'border-link border-2 shadow-md' : 'border-gray-200 hover:border-gray-300'"
    data-test="media-image-tile"
  >
    <div class="absolute top-2 left-2 z-10">
      <Checkbox
        :name="`media-tile-${media.id}`"
        :label="`Select ${media.title || media.fileName}`"
        :model-value="isSelected"
        :show-label="false"
        @update:model-value="handleCheckboxChange"
      />
    </div>

    <div class="absolute top-2 right-2 z-10">
      <DropdownMenu :items="dropdownItems" side="bottom" align="end">
        <template #activator>
          <button
            type="button"
            class="bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white border-0 rounded-lg p-1.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
          >
            <Icon icon="MenuEllipsisHorizontal" size="sm" />
          </button>
        </template>
      </DropdownMenu>
    </div>

    <div
      class="relative aspect-square overflow-hidden bg-gray-100 cursor-pointer"
      @click.stop="handleCheckboxChange(!isSelected)"
      @dblclick.stop="emit('view', media)"
    >
      <img
        :src="media.getThumbnailUrl()"
        loading="lazy"
        :alt="media.title || media.fileName"
        class="w-full h-full object-cover transition-transform group-hover:scale-105"
        @contextmenu.prevent
      >

      <div class="absolute bottom-2 left-2 z-10 flex items-center gap-1.5">
        <div
          v-if="media.isPinned"
          data-test="pinned-indicator"
          class="bg-black/60 backdrop-blur-sm rounded px-2 py-1"
        >
          <Icon icon="StarSolid" size="sm" class="text-yellow-400" />
        </div>

        <div
          v-if="isVideo"
          class="bg-black/60 backdrop-blur-sm rounded px-2 py-1 flex items-center gap-1"
        >
          <Icon icon="Video" size="sm" class="text-white" />
          <span class="text-white text-xs font-medium">{{ t("video") }}</span>
        </div>
      </div>
    </div>

    <div class="p-3 bg-white text-gray-500">
      <div class="flex items-center gap-1">
        <Icon icon="Folder" class="text-gray-500" size="sm" />
        <span class="text-sm truncate" data-test="album">{{ media.album?.title ?? t("media_table.main_gallery") }}</span>
      </div>
      <div v-if="media.uploadedBy" class="text-sm truncate flex items-end gap-1" data-test="uploaded-by">
        <Icon icon="User" class="text-gray-500 shrink-0" size="sm" />
        <span class="truncate min-w-0">{{ media.uploadedBy }}</span>
      </div>
      <div
        v-if="media?.requiresModeration"
        class="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-[#FFF4CE] text-[#664400] text-xs font-medium"
      >
        <Icon icon="EyeOn" size="sm" />
        <span>{{ t("media_table.awaiting_approval") }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.group {
  transition: all 0.2s ease-in-out;
}
</style>
